import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';

import { DatePicker } from '@mui/x-date-pickers';
import { subDays, startOfDay, endOfDay, format } from 'date-fns';

import axiosInstance from 'utils/axiosUtil';
import { withRouter } from 'utils';
import { TitleTooltip, SmallIconTextButton } from 'components';
import { listMileageLogs } from 'store/actions/mileageActions';
import { listActiveUsers } from 'store/actions/usersActions';
import { showConfirmationDialog } from 'store/actions/systemActions';
import { StoreUtil } from 'doctivity-shared/utils';

import MileageDialog from './MileageDialog';

const minimumStartDate = new Date(2018, 0, 1);

// ── sessionStorage helpers ─────────────────────────────────────────────────────
const isValidDate = (d) => d instanceof Date && !isNaN(d);

function readSessionDates() {
  const startRaw = sessionStorage.getItem('startDate');
  const endRaw   = sessionStorage.getItem('endDate');
  const start    = startRaw ? new Date(startRaw) : null;
  const end      = endRaw   ? new Date(endRaw)   : null;
  return {
    startDate: startOfDay(isValidDate(start) ? start : subDays(new Date(), 30)),
    endDate:   endOfDay(isValidDate(end) ? end : new Date()),
  };
}

function readSessionUser() {
  const raw = sessionStorage.getItem('filterUser');
  try {
    return raw ? JSON.parse(raw) : 'All Users';
  } catch {
    return 'All Users';
  }
}

const styles = (theme) => ({
  // ── Content wrapper ────────────────────────────────────────────────────────
  content: {
    padding: theme.spacing(3),
  },

  // ── Filter bar (matches ProviderCommentsOptions) ───────────────────────────
  filters: {
    '& > *': {
      marginBottom: theme.spacing(1),
    },
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  filterSpace: {
    width: 4,
  },
  userFilter: {
    minWidth: 270,
  },
  rangeControl: {
    minWidth: 90,
  },

  // ── Card spacing ───────────────────────────────────────────────────────────
  sectionCard: {
    marginBottom: theme.spacing(3),
  },

  // ── Card header row ────────────────────────────────────────────────────────
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  spacer: {
    flex: 1,
  },

  // ── Table header / body — matches DynamicTable / Activity Notes ───────────
  tableHead: {
    backgroundColor: 'rgba(0,0,0,0.03)',
    '& th': {
      fontSize: 12,
      fontWeight: 'inherit',
      padding: 2,
      verticalAlign: 'top',
    },
  },
  tableBody: {
    fontWeight: 300,
    '& td': {
      fontSize: 12,
      padding: 2,
      fontWeight: 'inherit',
    },
  },
  tableRow: {
    height: 30,
    cursor: 'pointer',
  },
  hiddenOnMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },

  // ── Column widths (shared between both tables for visual alignment) ──────────
  colLastName:  { width: 100 },
  colFirstName: { width: 100 },
  colDate:      { width: 90  },
  colTotal:     { width: 75, textAlign: 'right' },
  colTripStart: {
    width: 150,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  colTripDestination: {
    width: 150,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  colPurpose: {
    width: 110,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  colRecipients: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // ── Table states ───────────────────────────────────────────────────────────
  summaryRow: {
    backgroundColor: theme.palette.grey[50],
  },
  emptyCell: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingCell: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
});

// ── Date formatting ────────────────────────────────────────────────────────────
// Safely formats a DATEONLY string ('YYYY-MM-DD') as 'MM/DD/YYYY' without
// timezone conversion (avoids off-by-one on systems west of UTC).
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const parts = String(dateStr).split('T')[0].split('-');
  if (parts.length !== 3) return dateStr;
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
}

// ── Client-side sort helper ────────────────────────────────────────────────────
function sortRows(rows, sortBy, sortDir, { dateField = 'date_of_trip' } = {}) {
  return [...rows].sort((a, b) => {
    let aVal, bVal;
    switch (sortBy) {
      case 'last_name':
        aVal = (a.user?.last_name  ?? '').toLowerCase();
        bVal = (b.user?.last_name  ?? '').toLowerCase();
        break;
      case 'first_name':
        aVal = (a.user?.first_name ?? '').toLowerCase();
        bVal = (b.user?.first_name ?? '').toLowerCase();
        break;
      case 'mileage':
        aVal = parseFloat(a.mileage) || 0;
        bVal = parseFloat(b.mileage) || 0;
        break;
      case 'reimbursement':
        aVal = parseFloat(a.reimbursement) || 0;
        bVal = parseFloat(b.reimbursement) || 0;
        break;
      case 'total_amount':
        aVal = parseFloat(a.total_amount) || 0;
        bVal = parseFloat(b.total_amount) || 0;
        break;
      default: // 'date' — sort by the date the user entered; tiebreak by created_at
        // DATEONLY fields sort correctly as 'YYYY-MM-DD' strings; appending
        // the created_at ISO timestamp breaks ties within the same day.
        aVal = (a[dateField] || '') + '|' + (a.created_at || '');
        bVal = (b[dateField] || '') + '|' + (b.created_at || '');
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });
}

class MileageTrackingPage extends Component {
  constructor(props) {
    super(props);
    const { startDate, endDate } = readSessionDates();

    // If the user has never set a preference, default non-admins to their own
    // logs so they immediately see their own data rather than an empty "All Users" view.
    let filterUser = readSessionUser();
    if (!sessionStorage.getItem('filterUser') && !props.isAdmin && props.currentUser) {
      filterUser = props.currentUser;
    }

    this.state = {
      startDate,
      endDate,
      filterUser,

      exportErrors: [],
      mileageDialogOpen: false,
      mileagePage: 0,
      mileageRowsPerPage: 25,
      mileageSortBy: 'date',
      mileageSortDir: 'desc',
    };
  }

  componentDidMount() {
    const { dispatch, selectedClient } = this.props;
    if (selectedClient) dispatch(listActiveUsers(selectedClient));
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    const { dispatch, selectedClient } = this.props;
    if (selectedClient && selectedClient !== prevProps.selectedClient) {
      dispatch(listActiveUsers(selectedClient));
      this.loadData();
    }
  }

  // ── Derive ISO date strings for API calls ──────────────────────────────────
  _startStr = () => {
    const { startDate } = this.state;
    return startDate ? format(startDate, 'yyyy-MM-dd') : null;
  };

  _endStr = () => {
    const { endDate } = this.state;
    return endDate ? format(endDate, 'yyyy-MM-dd') : null;
  };

  _filterUserId = () => {
    const { filterUser } = this.state;
    return (filterUser && filterUser !== 'All Users') ? filterUser.id : null;
  };

  loadData = () => {
    const { dispatch, selectedClient } = this.props;
    if (!selectedClient) return;
    const startStr     = this._startStr();
    const endStr       = this._endStr();
    const filterUserId = this._filterUserId();

    dispatch(listMileageLogs(selectedClient, startStr, endStr, filterUserId));
  };

  onUserChange = (_event, newUser) => {
    sessionStorage.setItem('filterUser', JSON.stringify(newUser));
    this.setState({ filterUser: newUser, mileagePage: 0 }, this.loadData);
  };

  onStartDateChange = (newDate) => {
    if (!newDate || isNaN(newDate)) return;
    const d = startOfDay(newDate);
    sessionStorage.setItem('startDate', d.toISOString());
    this.setState({ startDate: d, mileagePage: 0 }, this.loadData);
  };

  onEndDateChange = (newDate) => {
    if (!newDate || isNaN(newDate)) return;
    const d = endOfDay(newDate);
    sessionStorage.setItem('endDate', d.toISOString());
    this.setState({ endDate: d, mileagePage: 0 }, this.loadData);
  };

  // ── Mileage ──────────────────────────────────────────────────────────────

  onMileageSaved = () => {
    this.loadData();
    this.setState({ mileageDialogOpen: false });
  };

  openMileageDialog = () => {
    const { dispatch, isAdmin } = this.props;
    if (isAdmin) {
      dispatch(
        showConfirmationDialog({
          title: 'Admin Users Can Not Add Mileage',
          content: 'Login as a non-admin user to log mileage.',
          confirmLabel: 'OK',
          onConfirm: () => {},
        })
      );
    } else {
      this.setState({ mileageDialogOpen: true });
    }
  };

  exportMileageCsv = async () => {
    const { selectedClient } = this.props;
    const startStr     = this._startStr();
    const endStr       = this._endStr();
    const filterUserId = this._filterUserId();
    try {
      const res = await axiosInstance.get('/v2/mileage/export', {
        params: {
          clientId: selectedClient,
          ...(startStr     ? { startDate: startStr }     : {}),
          ...(endStr       ? { endDate: endStr }         : {}),
          ...(filterUserId != null ? { filterUserId }    : {}),
        },
        responseType: 'blob',
      });
      const fromLabel = startStr ? startStr.replace(/-/g, '') : 'all';
      const toLabel   = endStr   ? endStr.replace(/-/g, '')   : 'all';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mileage_${fromLabel}_${toLabel}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('mileage export error', err);
      this.setState({ exportErrors: [err.message || 'Mileage export failed.'] });
    }
  };

  formatCurrency = (val) => {
    if (val === null || val === undefined) return '—';
    return `$${parseFloat(val).toFixed(2)}`;
  };

  onMileagePageChange = (_e, newPage) => this.setState({ mileagePage: newPage });
  onMileageRowsPerPageChange = (e) =>
    this.setState({ mileageRowsPerPage: parseInt(e.target.value, 10), mileagePage: 0 });

  onMileageSortChange = (column) => {
    this.setState((prev) => ({
      mileageSortBy: column,
      mileageSortDir: prev.mileageSortBy === column
        ? (prev.mileageSortDir === 'asc' ? 'desc' : 'asc')
        : 'asc',
      mileagePage: 0,
    }));
  };

  render() {
    const {
      classes,
      mileageLogs, mileageLoading,
      selectedClient,
      users,
    } = this.props;
    const {
      startDate, endDate, filterUser,
      mileageDialogOpen,
      exportErrors,
      mileagePage, mileageRowsPerPage,
      mileageSortBy, mileageSortDir,
    } = this.state;

    const showingAll  = !filterUser || filterUser === 'All Users';
    const userOptions = ['All Users', ...(StoreUtil.isLoaded(users) ? StoreUtil.getData(users) : [])];

    const logs = mileageLogs || [];

    // Sorted + paginated slices — reimbursement comes pre-computed per row from the backend
    const sortedLogs = sortRows(logs, mileageSortBy, mileageSortDir, { dateField: 'date_of_trip' });
    const pagedLogs  = sortedLogs.slice(
      mileagePage * mileageRowsPerPage,
      mileagePage * mileageRowsPerPage + mileageRowsPerPage,
    );

    return (
      <>
        <title>Tracking</title>

        <div className={classes.content}>

          {/* ── Filter bar — left-aligned, matches Reports / All pages ─────── */}
          <div className={classes.filters}>
            <FormControl className={classes.userFilter}>
              <Autocomplete
                options={userOptions}
                getOptionLabel={(u) => u === 'All Users' ? 'All Users' : `${u.last_name}, ${u.first_name}`}
                isOptionEqualToValue={(option, value) =>
                  option === value ||
                  (option !== 'All Users' && value !== 'All Users' && option.id === value.id)
                }
                autoComplete
                disableClearable
                value={filterUser}
                onChange={this.onUserChange}
                renderInput={(params) => <TextField {...params} label='User' />}
              />
            </FormControl>

            <div className={classes.filterSpace} />

            <FormControl className={classes.rangeControl}>
              <DatePicker
                label='From'
                value={startDate}
                minDate={minimumStartDate}
                maxDate={endDate || new Date()}
                onChange={this.onStartDateChange}
                renderInput={(params) => <TextField {...params} />}
              />
            </FormControl>

            <div className={classes.filterSpace} />

            <FormControl className={classes.rangeControl}>
              <DatePicker
                label='To'
                value={endDate}
                minDate={startDate || minimumStartDate}
                maxDate={new Date()}
                onChange={this.onEndDateChange}
                renderInput={(params) => (
                  <TextField {...params} inputProps={{ ...params.inputProps, readOnly: true }} />
                )}
              />
            </FormControl>
          </div>

          {/* ════════════════════════════════════════════════════════════ */}
          {/* MILEAGE SECTION                                             */}
          {/* ════════════════════════════════════════════════════════════ */}

          <Card className={classes.sectionCard}>
            <CardContent>
              <div className={classes.cardHeader}>
                <TitleTooltip
                  title='Mileage'
                  onExport={this.exportMileageCsv}
                />
                <div className={classes.spacer} />
                <SmallIconTextButton
                  icon='add'
                  text='Log Mileage'
                  onClick={this.openMileageDialog}
                />
              </div>

              <Table size='small' sx={{ tableLayout: 'fixed' }}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    {showingAll && (
                      <TableCell className={classes.hiddenOnMobile} style={{ width: 100 }}>
                        <TableSortLabel
                          active={mileageSortBy === 'last_name'}
                          direction={mileageSortBy === 'last_name' ? mileageSortDir : 'asc'}
                          onClick={() => this.onMileageSortChange('last_name')}
                        >
                          Last Name
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {showingAll && (
                      <TableCell className={classes.hiddenOnMobile} style={{ width: 100 }}>
                        <TableSortLabel
                          active={mileageSortBy === 'first_name'}
                          direction={mileageSortBy === 'first_name' ? mileageSortDir : 'asc'}
                          onClick={() => this.onMileageSortChange('first_name')}
                        >
                          First Name
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell style={{ width: 90 }}>
                      <TableSortLabel
                        active={mileageSortBy === 'date'}
                        direction={mileageSortBy === 'date' ? mileageSortDir : 'asc'}
                        onClick={() => this.onMileageSortChange('date')}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.colTripStart}>Trip Start</TableCell>
                    <TableCell className={`${classes.colTripDestination} ${classes.hiddenOnMobile}`}>Trip Destination</TableCell>
                    <TableCell className={`${classes.colPurpose} ${classes.hiddenOnMobile}`}>Purpose</TableCell>
                    <TableCell align='right' style={{ width: 70 }}>
                      <TableSortLabel
                        active={mileageSortBy === 'mileage'}
                        direction={mileageSortBy === 'mileage' ? mileageSortDir : 'asc'}
                        onClick={() => this.onMileageSortChange('mileage')}
                      >
                        Miles
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='right' className={classes.hiddenOnMobile} style={{ width: 110 }}>
                      <TableSortLabel
                        active={mileageSortBy === 'reimbursement'}
                        direction={mileageSortBy === 'reimbursement' ? mileageSortDir : 'asc'}
                        onClick={() => this.onMileageSortChange('reimbursement')}
                      >
                        Reimbursement
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className={classes.tableBody}>
                  {mileageLoading ? (
                    <TableRow>
                      <TableCell colSpan={showingAll ? 8 : 6} className={classes.loadingCell}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={showingAll ? 8 : 6} className={classes.emptyCell} sx={{ py: '24px !important' }}>
                        No mileage logged for the selected date range.{' '}
                        {filterUser && filterUser !== 'All Users' && filterUser.id === this.props.currentUserId && (
                          <Button
                            size='small'
                            color='primary'
                            onClick={this.openMileageDialog}
                          >
                            Log your first trip
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedLogs.map((log) => (
                      <TableRow
                        key={log.id}
                        hover
                        className={classes.tableRow}
                        onClick={() =>
                          this.props.router.navigate(`/notes/mileage/${log.id}`)
                        }
                      >
                        {showingAll && (
                          <TableCell className={classes.hiddenOnMobile}>
                            {log.user?.last_name || '—'}
                          </TableCell>
                        )}
                        {showingAll && (
                          <TableCell className={classes.hiddenOnMobile}>
                            {log.user?.first_name || '—'}
                          </TableCell>
                        )}
                        <TableCell>{formatDate(log.date_of_trip)}</TableCell>
                        <TableCell className={classes.colTripStart}>{log.trip_start || '—'}</TableCell>
                        <TableCell className={`${classes.colTripDestination} ${classes.hiddenOnMobile}`}>{log.trip_destination || '—'}</TableCell>
                        <TableCell className={`${classes.colPurpose} ${classes.hiddenOnMobile}`}>{log.purpose || '—'}</TableCell>
                        <TableCell align='right'>
                          {parseFloat(log.mileage).toFixed(1)}
                        </TableCell>
                        <TableCell align='right' className={classes.hiddenOnMobile}>
                          {log.is_business
                            ? `$${parseFloat(log.reimbursement || 0).toFixed(2)}`
                            : '$0.00'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}

                </TableBody>
              </Table>
              {logs.length > 0 && (
                <TablePagination
                  component='div'
                  count={logs.length}
                  rowsPerPage={mileageRowsPerPage}
                  page={mileagePage}
                  backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                  nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from.toLocaleString()}-${to.toLocaleString()} of ${count.toLocaleString()}`
                  }
                  onPageChange={this.onMileagePageChange}
                  onRowsPerPageChange={this.onMileageRowsPerPageChange}
                  rowsPerPageOptions={[10, 25, 50, 100, 200]}
                />
              )}
            </CardContent>
          </Card>

        </div>

        {/* ── Mileage dialog (new log only — editing via detail page) ───── */}
        <MileageDialog
          open={mileageDialogOpen}
          onClose={() => this.setState({ mileageDialogOpen: false })}
          onSaved={this.onMileageSaved}
          clientId={selectedClient}
        />

        {/* ── Export validation errors dialog ────────────────────────────── */}
        <Dialog
          open={exportErrors.length > 0}
          onClose={() => this.setState({ exportErrors: [] })}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>Export Cannot Be Generated</DialogTitle>
          <DialogContent>
            <Typography variant='body2' gutterBottom>
              The following issues must be resolved before this data can be exported
              in CMS format:
            </Typography>
            <List dense disablePadding>
              {exportErrors.map((err, i) => (
                <ListItem key={i} disableGutters>
                  <ListItemText
                    primary={err}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.setState({ exportErrors: [] })}
              variant='contained'
              color='primary'
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

MileageTrackingPage.propTypes = {
  classes:        PropTypes.object.isRequired,
  dispatch:       PropTypes.func.isRequired,
  router:         PropTypes.object.isRequired,
  selectedClient: PropTypes.number,
  currentUserId:  PropTypes.number,
  currentUser:    PropTypes.object,
  isAdmin:        PropTypes.bool,
  mileageLogs:    PropTypes.array,
  mileageLoading: PropTypes.bool,
  users:          PropTypes.object,
};

function mapStateToProps(state) {
  const u = state.user || null;
  return {
    selectedClient: state.app?.selectedClient || null,
    currentUserId:  u?.id || null,
    currentUser:    u ? { id: u.id, first_name: u.first_name, last_name: u.last_name } : null,
    isAdmin:        u?.is_admin || false,
    mileageLogs:    state.mileage?.logs?.data || [],
    mileageLoading: state.mileage?.logs?.loading || false,
    users:          state.users?.list,
  };
}

export default connect(mapStateToProps)(withRouter(withStyles(styles)(MileageTrackingPage)));
